/**
 * Canvas Store - Primary state for canvas entities
 *
 * This store represents the canvas as the source of truth, replacing the
 * flowSpec-centric approach. It manages nodes and edges with their visual
 * properties and relationships.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Position on the canvas
 */
export interface CanvasPosition {
  x: number;
  y: number;
}

/**
 * Visual properties for canvas elements
 */
export interface VisualProperties {
  deletable: boolean;
  width?: number;
  height?: number;
}

/**
 * Canvas Node - Represents a component on the canvas
 */
export interface CanvasNode extends VisualProperties {
  id: string;
  position: CanvasPosition;
  tags: string[]; // e.g., ['component', 'choice', 'subpipeline']
  parentId?: string; // For nested components (onProcess/onException)
}

/**
 * Canvas Edge - Represents a connection between nodes
 */
export interface CanvasEdge extends VisualProperties {
  id: string;
  sourceId: string;
  targetId: string;
  tags: string[]; // e.g., ['default', 'choice', 'parallel-execution']
  metadata?: {
    conditionType?: 'when' | 'otherwise';
    label?: string;
    description?: string;
    executionId?: string;
  };
}

/**
 * Level path item for tracking nested levels
 */
export interface LevelPathItem {
  label: string;
  type: 'start' | 'onProcess' | 'onException';
  parent?: string;
}

/**
 * Canvas Store State
 */
export interface CanvasState {
  // Core entities
  nodes: Map<string, CanvasNode>;
  edges: Map<string, CanvasEdge>;

  // Current level context
  currentLevelPath: LevelPathItem[];
  currentLevelName: string;

  // Selection state
  selectedNodeIds: Set<string>;
  selectedEdgeIds: Set<string>;

  // Actions - Nodes
  addNode: (node: CanvasNode, isSelected?: boolean) => void;
  addNodes: (nodes: CanvasNode[]) => void;
  removeNode: (nodeId: string) => void;
  removeNodes: (nodeIds: string[]) => void;
  updateNode: (nodeId: string, updates: Partial<CanvasNode>) => void;
  updateNodePosition: (nodeId: string, position: CanvasPosition) => void;
  updateNodesPositions: (
    updates: Array<{ nodeId: string; position: CanvasPosition }>
  ) => void;
  getNode: (nodeId: string) => CanvasNode | undefined;
  getNodesByTag: (tag: string) => CanvasNode[];
  getNodesByParent: (parentId: string) => CanvasNode[];

  // Actions - Edges
  addEdge: (edge: CanvasEdge) => void;
  addEdges: (edges: CanvasEdge[]) => void;
  removeEdge: (edgeId: string) => void;
  removeEdges: (edgeIds: string[]) => void;
  updateEdge: (edgeId: string, updates: Partial<CanvasEdge>) => void;
  getEdge: (edgeId: string) => CanvasEdge | undefined;
  getEdgesBySource: (sourceId: string) => CanvasEdge[];
  getEdgesByTarget: (targetId: string) => CanvasEdge[];
  getEdgesByTag: (tag: string) => CanvasEdge[];

  // Actions - Selection
  selectNode: (nodeId: string) => void;
  selectNodes: (nodeIds: string[]) => void;
  deselectNode: (nodeId: string) => void;
  deselectAllNodes: () => void;
  selectEdge: (edgeId: string) => void;
  selectEdges: (edgeIds: string[]) => void;
  deselectEdge: (edgeId: string) => void;
  deselectAllEdges: () => void;
  selectAll: () => void;
  deselectAll: () => void;

  // Actions - Level Navigation
  setCurrentLevel: (levelPath: LevelPathItem[], levelName: string) => void;

  // Actions - Batch Operations
  clearCanvas: () => void;
  clearNodes: () => void;
  clearEdges: () => void;
  replaceCanvas: (nodes: CanvasNode[], edges: CanvasEdge[]) => void;
  isDownstreamOf: (sourceId: string, targetId: string) => boolean;
  reset: () => void;
}

/**
 * Create Canvas Store
 */
export const useCanvasStore = create<CanvasState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      nodes: new Map(),
      edges: new Map(),
      currentLevelPath: [{ label: 'root', type: 'start' }],
      currentLevelName: 'start',
      selectedNodeIds: new Set(),
      selectedEdgeIds: new Set(),

      // Node actions
      addNode: (node, isSelected) =>
        set(state => {
          state.nodes.set(node.id, node);
          if (isSelected) {
            state.selectedNodeIds.clear();
            state.selectedNodeIds.add(node.id);
          }
        }),

      addNodes: nodes =>
        set(state => {
          nodes.forEach(node => {
            state.nodes.set(node.id, node);
          });
        }),

      removeNode: nodeId =>
        set(state => {
          state.nodes.delete(nodeId);
          state.selectedNodeIds.delete(nodeId);

          // Remove connected edges
          const edgesToRemove: string[] = [];
          state.edges.forEach((edge, edgeId) => {
            if (edge.sourceId === nodeId || edge.targetId === nodeId) {
              edgesToRemove.push(edgeId);
            }
          });
          edgesToRemove.forEach(edgeId => {
            state.edges.delete(edgeId);
            state.selectedEdgeIds.delete(edgeId);
          });
        }),

      removeNodes: nodeIds =>
        set(state => {
          nodeIds.forEach(nodeId => {
            state.nodes.delete(nodeId);
            state.selectedNodeIds.delete(nodeId);
          });

          // Remove connected edges
          const edgesToRemove: string[] = [];
          state.edges.forEach((edge, edgeId) => {
            if (
              nodeIds.includes(edge.sourceId) ||
              nodeIds.includes(edge.targetId)
            ) {
              edgesToRemove.push(edgeId);
            }
          });
          edgesToRemove.forEach(edgeId => {
            state.edges.delete(edgeId);
            state.selectedEdgeIds.delete(edgeId);
          });
        }),

      updateNode: (nodeId, updates) =>
        set(state => {
          const node = state.nodes.get(nodeId);
          if (node) {
            state.nodes.set(nodeId, { ...node, ...updates });
          }
        }),

      updateNodePosition: (nodeId, position) =>
        set(state => {
          const node = state.nodes.get(nodeId);
          if (node) {
            node.position = position;
          }
        }),

      updateNodesPositions: (
        updates: Array<{ nodeId: string; position: CanvasPosition }>
      ) =>
        set(state => {
          updates.forEach(({ nodeId, position }) => {
            const node = state.nodes.get(nodeId);
            if (node) {
              node.position = position;
            }
          });
        }),

      getNode: nodeId => get().nodes.get(nodeId),

      getNodesByTag: tag => {
        const { nodes } = get();
        return Array.from(nodes.values()).filter(node =>
          node.tags.includes(tag)
        );
      },

      getNodesByParent: parentId => {
        const { nodes } = get();
        return Array.from(nodes.values()).filter(
          node => node.parentId === parentId
        );
      },

      // Edge actions
      addEdge: edge =>
        set(state => {
          state.edges.set(edge.id, edge);
        }),

      addEdges: edges =>
        set(state => {
          edges.forEach(edge => {
            state.edges.set(edge.id, edge);
          });
        }),

      removeEdge: edgeId =>
        set(state => {
          state.edges.delete(edgeId);
          state.selectedEdgeIds.delete(edgeId);
        }),

      removeEdges: edgeIds =>
        set(state => {
          edgeIds.forEach(edgeId => {
            state.edges.delete(edgeId);
            state.selectedEdgeIds.delete(edgeId);
          });
        }),

      updateEdge: (edgeId, updates) =>
        set(state => {
          const edge = state.edges.get(edgeId);
          if (edge) {
            state.edges.set(edgeId, { ...edge, ...updates });
          }
        }),

      getEdge: edgeId => get().edges.get(edgeId),

      getEdgesBySource: sourceId => {
        const { edges } = get();
        return Array.from(edges.values()).filter(
          edge => edge.sourceId === sourceId
        );
      },

      getEdgesByTarget: targetId => {
        const { edges } = get();
        return Array.from(edges.values()).filter(
          edge => edge.targetId === targetId
        );
      },

      getEdgesByTag: tag => {
        const { edges } = get();
        return Array.from(edges.values()).filter(edge =>
          edge.tags.includes(tag)
        );
      },

      // Selection actions
      selectNode: nodeId =>
        set(state => {
          state.selectedNodeIds.add(nodeId);
        }),

      selectNodes: nodeIds =>
        set(state => {
          nodeIds.forEach(nodeId => {
            state.selectedNodeIds.add(nodeId);
          });
        }),

      deselectNode: nodeId =>
        set(state => {
          state.selectedNodeIds.delete(nodeId);
        }),

      deselectAllNodes: () =>
        set(state => {
          state.selectedNodeIds.clear();
        }),

      selectEdge: edgeId =>
        set(state => {
          state.selectedEdgeIds.add(edgeId);
        }),

      selectEdges: edgeIds =>
        set(state => {
          edgeIds.forEach(edgeId => {
            state.selectedEdgeIds.add(edgeId);
          });
        }),

      deselectEdge: edgeId =>
        set(state => {
          state.selectedEdgeIds.delete(edgeId);
        }),

      deselectAllEdges: () =>
        set(state => {
          state.selectedEdgeIds.clear();
        }),

      selectAll: () =>
        set(state => {
          state.nodes.forEach((node, nodeId) => {
            state.selectedNodeIds.add(nodeId);
          });
          state.edges.forEach((edge, edgeId) => {
            state.selectedEdgeIds.add(edgeId);
          });
        }),

      deselectAll: () =>
        set(state => {
          state.selectedNodeIds.clear();
          state.selectedEdgeIds.clear();
        }),

      // Level navigation
      setCurrentLevel: (levelPath, levelName) =>
        set(state => {
          state.currentLevelPath = levelPath;
          state.currentLevelName = levelName;
        }),

      // Batch operations
      clearCanvas: () =>
        set(state => {
          state.nodes.clear();
          state.edges.clear();
          state.selectedNodeIds.clear();
          state.selectedEdgeIds.clear();
        }),

      clearNodes: () =>
        set(state => {
          state.nodes.clear();
          state.selectedNodeIds.clear();
        }),

      clearEdges: () =>
        set(state => {
          state.edges.clear();
          state.selectedEdgeIds.clear();
        }),

      replaceCanvas: (nodes, edges) =>
        set(state => {
          state.nodes.clear();
          state.edges.clear();
          state.selectedNodeIds.clear();
          state.selectedEdgeIds.clear();

          nodes.forEach(node => {
            state.nodes.set(node.id, node);
          });

          edges.forEach(edge => {
            state.edges.set(edge.id, edge);
          });
        }),

      isDownstreamOf: (sourceId: string, targetId: string) => {
        const { edges, getEdgesBySource } = get();
        const visited = new Set<string>();
        const queue: string[] = [targetId];

        while (queue.length > 0) {
          const currentId = queue.shift()!;
          if (currentId === sourceId) {
            return true;
          }
          if (visited.has(currentId)) {
            continue;
          }

          visited.add(currentId);

          const outgoingEdges = getEdgesBySource(currentId);

          outgoingEdges.forEach(edge => {
            if (!visited.has(edge.targetId)) {
              queue.push(edge.targetId);
            }
          });
        }

        return false;
      },

      reset: () =>
        set(state => {
          state.nodes = new Map();
          state.edges = new Map();
          state.currentLevelPath = [{ label: 'root', type: 'start' }];
          state.currentLevelName = 'start';
          state.selectedNodeIds = new Set();
          state.selectedEdgeIds = new Set();
        })
    })),
    { name: 'CanvasStore' }
  )
);
